import { memo } from "react";
import { useShallow } from "zustand/shallow";
import { ChevronLeft, ChevronRight, X, Play } from "lucide-react";
import { useMapRouteStore } from "@/stores/mapRouteStore";

const formatTime = (seconds: number) => {
  if (seconds === 0) return null;
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} мин`;
};

const NavigationControls = memo(function NavigationControls() {
  const {
    direction,
    isNavigationMode,
    navigationStepIndex,
    startNavigation,
    stopNavigation,
    navigateNext,
    navigatePrev,
  } = useMapRouteStore(
    useShallow((s) => ({
      direction: s.direction,
      isNavigationMode: s.isNavigationMode,
      navigationStepIndex: s.navigationStepIndex,
      startNavigation: s.startNavigation,
      stopNavigation: s.stopNavigation,
      navigateNext: s.navigateNext,
      navigatePrev: s.navigatePrev,
    })),
  );

  if (!direction) return null;

  const segments = direction.segmentationRoute.segments;
  const totalSteps = segments.length;

  // Кнопка "Поехали" когда режим навигации не активен
  if (!isNavigationMode) {
    return (
      <div
        className="shrink-0 px-4 py-3"
        style={
          {
            // background: "linear-gradient(135deg, #00205B 0%, #1a3a7a 100%)",
          }
        }
      >
        <button
          onClick={startNavigation}
          className="w-full h-11 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 cursor-pointer shadow-lg"
          style={{
            background: "linear-gradient(135deg, #1a5fb4 0%, #0d3a7a 100%)",
            boxShadow: "0 2px 8px rgba(13, 58, 122, 0.4)",
          }}
        >
          <Play className="w-4 h-4 fill-current" />
          Поехали
        </button>
      </div>
    );
  }

  // Панель навигации когда режим активен
  const currentSegment = segments[navigationStepIndex];
  const isFirst = navigationStepIndex === 0;
  const isLast = navigationStepIndex === totalSteps - 1;

  return (
    <div
      className="shrink-0 px-4 py-3"
      style={
        {
          // background: "linear-gradient(135deg, #00205B 0%, #1a3a7a 100%)",
        }
      }
    >
      {/* Основная панель навигации */}
      <div
        className="flex items-stretch bg-black/20 rounded-xl overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #1a5fb4 0%, #0d3a7a 100%)",
          boxShadow: "0 2px 8px rgba(13, 58, 122, 0.4)",
        }}
      >
        {/* Кнопка "Назад" */}
        <button
          onClick={navigatePrev}
          disabled={isFirst}
          className={`w-12 flex items-center justify-center border-r border-white/10 transition-colors ${
            isFirst
              ? "opacity-25 cursor-default"
              : "hover:bg-white/10 active:bg-white/15 cursor-pointer"
          }`}
        >
          <ChevronLeft
            className="w-[22px] h-[22px] text-white"
            strokeWidth={2.5}
          />
        </button>

        {/* Центр: информация о сегменте */}
        <div className="flex-1 relative h-[72px] px-2 min-w-0">
          {/* Счётчик сверху */}
          <span className="absolute top-2 left-0 right-0 text-center text-[11px] text-white/50 font-medium">
            {navigationStepIndex + 1} / {totalSteps}
          </span>

          {/* Title по центру */}
          <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold text-white text-center truncate px-2">
            {currentSegment.title}
          </span>

          {/* Subtitle и Time снизу */}
          <div className="absolute bottom-2 left-0 right-0 flex flex-col items-center">
            {currentSegment.subtitle && (
              <span className="text-xs text-white/60 truncate max-w-full">
                {currentSegment.subtitle}
              </span>
            )}
            {currentSegment.time > 0 && (
              <span className="text-[11px] text-[#C4A962] font-semibold">
                {formatTime(currentSegment.time)}
              </span>
            )}
          </div>
        </div>

        {/* Кнопка "Вперёд" */}
        <button
          onClick={navigateNext}
          disabled={isLast}
          className={`w-12 flex items-center justify-center border-l border-white/10 transition-colors ${
            isLast
              ? "opacity-25 cursor-default"
              : "hover:bg-white/10 active:bg-white/15 cursor-pointer"
          }`}
        >
          <ChevronRight
            className="w-[22px] h-[22px] text-white"
            strokeWidth={2.5}
          />
        </button>
      </div>

      {/* Кнопка завершить */}
      <div className="flex justify-center mt-2.5">
        <button
          onClick={stopNavigation}
          className="flex items-center gap-1.5 px-3.5 py-1.5 bg-[#1a5fb4]/80 border border-white/20 rounded-full text-white/90 text-xs hover:bg-[#1a5fb4] hover:text-white transition-colors cursor-pointer"
        >
          <X className="w-3.5 h-3.5" />
          Завершить
        </button>
      </div>
    </div>
  );
});

export default NavigationControls;
